import React, { useState, useEffect } from 'react'
import { ChevronRight, ChevronDown, Loader2, User, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { getFirestore, doc, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import styles from './IncomeBreakdownTable.module.css'
import { type IncomeBreakdownResult, type PartyBreakdown, type SectionBreakdown, type ComponentFactValues } from '../../../../../types/incomeBreakdown.ts'
import { UnderwritingFlags } from '../UnderwritingFlags.tsx'

const columnsNumber = 3
const combinedSubTypes = ['Combined', 'Final Calc'].map((i) => i.toLowerCase())

interface IncomeBreakdownTableProps {
  caseId: string
  onViewDocument?: (documentId: string) => void
}

export const IncomeBreakdownTable: React.FC<IncomeBreakdownTableProps> = ({
  caseId,
  onViewDocument
}) => {
  const [breakdown, setBreakdown] = useState<IncomeBreakdownResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  // Subscribe to breakdown from Firebase with real-time updates
  useEffect(() => {
    if (!caseId) {
      console.warn('[IncomeBreakdownTable] No caseId provided')
      setLoading(false)
      return
    }

    console.log('[IncomeBreakdownTable] Setting up Firebase listener for case:', caseId)

    const db = getFirestore()
    const breakdownRef = doc(db, 'cases', caseId, 'income_breakdown', 'main')

    const unsubscribe: Unsubscribe = onSnapshot(
      breakdownRef,
      (breakdownDoc) => {
        try {
          console.log('[IncomeBreakdownTable] Snapshot received, exists:', breakdownDoc.exists())

          if (breakdownDoc.exists()) {
            const rawData = breakdownDoc.data() as IncomeBreakdownResult
            console.log('[IncomeBreakdownTable] Loaded breakdown:', rawData)

            setBreakdown(rawData)
            setError(null)
            setLoading(false)

            // Auto-expand sections with non-zero monthly values
            const autoExpand = new Set<string>()
            rawData.parties?.forEach((party, partyIdx) => {
              party.sections?.forEach((section, sectionIdx) => {
                // Check if section has non-zero monthly income
                const hasNonZeroIncome = section.subtotal?.monthly !== 0 ||
                  section.components?.some(c => c.monthly !== 0)

                if (hasNonZeroIncome) {
                  autoExpand.add(`${party.party_id}_section_${sectionIdx}`)
                }
              })
            })
            setExpandedSections(autoExpand)
          } else {
            console.log('[IncomeBreakdownTable] No breakdown document found')
            setLoading(false)
            setError('No income breakdown data available yet')
          }
        } catch (err) {
          console.error('[IncomeBreakdownTable] Error processing snapshot:', err)
          setError(err instanceof Error ? err.message : 'Failed to load breakdown')
          setLoading(false)
        }
      },
      (err) => {
        console.error('[IncomeBreakdownTable] Snapshot error:', err)
        setError(err.message || 'Failed to subscribe to breakdown updates')
        setLoading(false)
      }
    )

    return () => {
      console.log('[IncomeBreakdownTable] Cleaning up listener')
      unsubscribe()
    }
  }, [caseId])

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  const formatCurrency = (amount: number): string => {
    if (amount === 0) return '-'
    const absAmount = Math.abs(amount)
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absAmount)
    return amount < 0 ? `-${formatted}` : formatted
  }

  const getTrendIcon = () => {
    if (!breakdown) return null

    switch (breakdown.income_trend) {
      case 'increasing':
        return <TrendingUp className={styles.trendIconIncrease} size={16} />
      case 'declining':
        return <TrendingDown className={styles.trendIconDecrease} size={16} />
      default:
        return <Minus className={styles.trendIconStable} size={16} />
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={32} />
        <p>Loading income breakdown...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
      </div>
    )
  }

  if (!breakdown) {
    return (
      <div className={styles.emptyContainer}>
        <p>No income breakdown available</p>
        <p className={styles.emptyHint}>Complete document scanning to generate breakdown</p>
      </div>
    )
  }

  return (
    <div className={styles.breakdownContainer}>
      {/* Header */}
      {/* <div className={styles.breakdownHeader}>
        <div className={styles.headerTitle}>
          <h3>Income Breakdown</h3>
          <span className={styles.loanTypeBadge}>{breakdown.loan_type.replace('_', ' ').toUpperCase()}</span>
        </div>
        <div className={styles.headerInfo}>
          <span>Tax Years: {breakdown.y1_year} - {breakdown.y2_year}</span>
          <span className={styles.trendBadge}>
            {getTrendIcon()}
            {breakdown.income_trend.charAt(0).toUpperCase() + breakdown.income_trend.slice(1)}
            {breakdown.trend_percentage !== 0 && ` (${breakdown.trend_percentage > 0 ? '+' : ''}${breakdown.trend_percentage.toFixed(1)}%)`}
          </span>
        </div>
      </div> */}

      {/* Income Flags */}
      {(breakdown.has_self_employment || breakdown.has_variable_income || breakdown.has_rental_income || breakdown.has_k1_income) && (
        <div className={styles.incomeFlagsContainer}>
          <div className={styles.flagsLabel}>Income Characteristics:</div>
          <div className={styles.flags}>
            {breakdown.has_self_employment && <span className={styles.flag}>Self-Employment</span>}
            {breakdown.has_variable_income && <span className={styles.flag}>Variable Income</span>}
            {breakdown.has_rental_income && <span className={styles.flag}>Rental Income</span>}
            {breakdown.has_k1_income && <span className={styles.flag}>K-1 Income</span>}
            {breakdown.gross_up_eligible && <span className={styles.flag}>Gross-Up Eligible</span>}
          </div>
        </div>
      )}

      {/* Breakdown Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.incomeTable}>
          <tbody>
            {breakdown.parties.map((party) => (
              <React.Fragment key={party.party_id}>
                {/* Party Header Row */}
                <tr className={styles.partyHeaderRow}>
                  <td colSpan={columnsNumber} className={styles.partyHeader}>
                    <User size={16} className={styles.partyIcon} />
                    <strong>{party.party_name}</strong>
                  </td>
                  <td className={styles.amountHeader}>Y1 ({breakdown.y1_year})</td>
                  <td className={styles.amountHeader}>Y2 ({breakdown.y2_year})</td>
                </tr>

                {/* Sections */}
                {[...party.sections].sort((a, b) => {
                  // If tableName matches, prioritize items with sub_type === "combined"
                  if (a.table_name === b.table_name) {
                    const aIsCombined = combinedSubTypes.includes(a.sub_type.toLowerCase())
                    const bIsCombined = combinedSubTypes.includes(b.sub_type.toLowerCase())
                    if (aIsCombined && !bIsCombined) return -1
                    if (!aIsCombined && bIsCombined) return 1
                    return 0
                  }
                  return 0
                }).map((section, sectionIdx) => {
                  const sectionId = `${party.party_id}_section_${sectionIdx}`
                  const isExpanded = expandedSections.has(sectionId)
                  const hasComponents = section.components && section.components.length > 0
                  const hasIncome = section.subtotal && section.subtotal.monthly !== 0

                  return (
                    <React.Fragment key={sectionId}>
                      {/* Section Header - Shows amounts even when collapsed */}
                      {combinedSubTypes.includes(section.sub_type.toLowerCase()) && (
                        <tr className={styles.spacerRow}>
                            <td
                              colSpan={columnsNumber}
                              role="presentation"
                              aria-hidden="true"
                            />
                        </tr>
                      )}
                      <tr
                        className={[
                          styles.sectionHeaderRow,
                          hasComponents ? styles.sectionHeaderClickable : ''
                        ].filter(Boolean).join(' ')}
                        onClick={() => hasComponents && toggleSection(sectionId)}
                      >
                        <td className={styles.sectionHeader}>
                          {hasComponents && (
                            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                          )}
                          <span className={styles.tableName}>{combinedSubTypes.includes(section.sub_type.toLowerCase()) ? `${section.table_name} - ` : ''}</span>
                          {section.sub_type && <span className={styles.subType}>{section.sub_type}</span>}
                        </td>
                        {/* Show subtotal amounts even when collapsed */}
                        <td className={styles.amountCell}>
                          {section.subtotal ? formatCurrency(section.subtotal.y1) : '$0.00'}
                        </td>
                        <td className={styles.amountCell}>
                          {section.subtotal ? formatCurrency(section.subtotal.y2) : '$0.00'}
                        </td>
                      </tr>


                      {/* Components (show when expanded) */}
                      {isExpanded && hasComponents && section.components.map((component) => (
                        <tr key={component.component} className={styles.componentRow}>
                          <td className={styles.componentCell}>
                            {component.description}
                            <span className={styles.componentSign}>({component.sign})</span>
                          </td>
                          <td className={styles.amountCell}>{formatCurrency(component.y1)}</td>
                          <td className={styles.amountCell}>{formatCurrency(component.y2)}</td>
                        </tr>
                      ))}

                    </React.Fragment>
                  )
                })}

                {/* Party Total */}
                <tr className={styles.spacerRow}>
                  <td colSpan={columnsNumber} />
                </tr>
                <tr className={styles.partyTotalRow}>
                  <td className={styles.partyTotalCell}>
                    <strong>Total - {party.party_name}</strong>
                  </td>
                  <td className={styles.amountCell}><strong>{formatCurrency(party.total.y1)}</strong></td>
                  <td className={styles.amountCell}><strong>{formatCurrency(party.total.y2)}</strong></td>
                </tr>
                <tr className={styles.spacerRow}>
                  <td colSpan={columnsNumber} />
                </tr>
              </React.Fragment>
            ))}

            {/* Combined Total (Grand Total) */}
            <tr className={styles.grandTotalRow}>
              <td className={styles.grandTotalCell}>
                <strong>COMBINED TOTAL</strong>
              </td>
              <td className={styles.amountCell}><strong>{formatCurrency(breakdown.combined_total.y1)}</strong></td>
              <td className={styles.amountCell}><strong>{formatCurrency(breakdown.combined_total.y2)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer with metadata */}
      <div className={styles.breakdownFooter}>
        <div className={styles.footerItem}>
          <strong>Monthly Qualifying Income:</strong> {formatCurrency(breakdown.combined_total.monthly)}
        </div>
      </div>

      {/* Underwriting Flags Section */}
      {breakdown.verification_flags && (
        <UnderwritingFlags flags={breakdown.verification_flags} />
      )}
    </div>
  )
}

export default IncomeBreakdownTable
